import { useEffect, useState } from 'react';
import { MCP_TOOL_NAMES } from '@servwell/shared';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTRPC } from '../../lib/trpc';
import { SectionCard } from '../ui/Detail';
import { Notice, StatusBadge } from '../ui/Badge';
import { Button } from '../ui/Button';
import { Toggle } from '../ui/Toggle';
import {
  StaleNotice,
  UnresolvedState,
  refreshFailed,
  shownState,
  unresolved,
} from '../ui/QueryStates';
import { IconSettings } from '../icons';
import { useSession } from '../session';

/**
 * Connect an AI to the shop, and see everything that is connected (PRD §4.3 / honest-terms
 * F4). Two halves that belong together for one reason: the place you grant access is the
 * place you revoke it. Splitting them is how a product ends up with a one-click connect and
 * a support ticket to disconnect.
 *
 * READ-ONLY is stated first and repeated, because it is the fact that decides whether an
 * owner does this at all.
 */

const TRY_IT = [
  "What's on tomorrow's schedule?",
  'Who owes us money right now, and who should I chase first?',
  'Which jobs lost money last month, and why?',
  "What's the full story on the Henderson job — what happened and where does it stand?",
  'Which maintenance agreements have visits coming up that are not booked yet?',
];

function CopyField({ value }: { value: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <div className="flex items-center gap-2">
      <code className="h-scroll min-w-0 flex-1 whitespace-nowrap rounded-[var(--radius-sm)] border border-border bg-surface-2 px-3 py-2 text-sm">
        {value}
      </code>
      <Button
        variant="ghost"
        onClick={() => {
          void navigator.clipboard?.writeText(value);
          setCopied(true);
          setTimeout(() => setCopied(false), 1500);
        }}
      >
        {copied ? 'Copied' : 'Copy'}
      </Button>
    </div>
  );
}

export function ConnectedAiCard({ id }: { id: string }) {
  const trpc = useTRPC();
  const qc = useQueryClient();
  const { role } = useSession();
  const isOwner = role === 'owner';
  const [error, setError] = useState<string | null>(null);
  const q = useQuery(trpc.aiConnection.list.queryOptions());

  const done = () => {
    setError(null);
    void qc.invalidateQueries();
  };
  const disconnect = useMutation(
    trpc.aiConnection.disconnect.mutationOptions({
      onSuccess: done,
      onError: (e) => setError(e.message),
    }),
  );
  const toggleOrgTool = useMutation(
    trpc.aiConnection.setOrgTool.mutationOptions({
      onSuccess: done,
      onError: (e) => setError(e.message),
    }),
  );
  const toggleTool = useMutation(
    trpc.aiConnection.setConnectionTool.mutationOptions({
      onSuccess: done,
      onError: (e) => setError(e.message),
    }),
  );

  /**
   * 🔴 There was no error branch at all, and the `?? ''` below is why nobody noticed.
   *
   * On a failed query the card rendered its not-connected variant with an EMPTY endpoint —
   * so a shop with three live AI connections was told it had none, handed a blank string to
   * copy, and `CopyField` answered "Copied". A blank copied over a real endpoint is worse
   * than an error, because the person walks away believing they have the thing.
   */
  const state = shownState(q, q.data);
  /**
   * ⚠️ Gated on HAVING NOTHING TO DRAW, not on the phase.
   *
   * 🔴 An earlier version said `state !== 'ready' || !q.data`. query-core keeps `data` on a
   * background error, so a refetch that failed on window focus — 30s `staleTime`, one flaky
   * Wi-Fi moment — replaced switches that were correct and on screen a second ago with
   * "Couldn't load". This card previously rendered its cached draft through exactly that
   * case, so the contract REGRESSED it.
   *
   * An error is only a reason to blank a surface when there is nothing left to show. The
   * phase still chooses the WORDS; data presence chooses whether to speak at all.
   */
  if (!q.data) {
    return (
      <SectionCard id={id} title="Connect your AI">
        <UnresolvedState
          state={unresolved(state)}
          what="your AI connections"
          icon={IconSettings}
          error={q.error}
          onRetry={() => void q.refetch()}
        />
      </SectionCard>
    );
  }

  const { mcpUrl, connections, orgDisabledTools: orgDisabled } = q.data;

  return (
    <SectionCard
      id={id}
      title={
        connections.length === 0
          ? 'Connect your AI'
          : `Connect your AI (${connections.length} connected)`
      }
    >
      {/*
        🔴 The one place where keeping cached content can state the OPPOSITE of what just
        happened, which is why `StaleNotice` exists at all. Disconnect: the mutation
        succeeds, `done()` invalidates, the refetch fails — and query-core hands back the
        pre-disconnect list, so this card goes on showing the connection as active. Found by
        the Codex adversary as an argument against silent staleness, and it is a good one.
      */}
      <StaleNotice show={refreshFailed(q, q.data)} onRetry={() => void q.refetch()} />
      {error ? <Notice tone="danger">{error}</Notice> : null}

      <p className="mb-2 text-sm text-muted-foreground">
        Point Claude or ChatGPT at your shop and ask questions in plain English.{' '}
        <strong className="text-text">It can only read.</strong> Nothing connected here
        can change, create or delete anything — not a job, not an invoice, not a customer.
      </p>

      <div className="space-y-2">
        <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          Your endpoint
        </p>
        <CopyField value={mcpUrl} />
        <p className="text-xs text-muted-foreground">
          Paste it into a custom connector. You sign in and approve once — there is no API
          key to keep safe.
        </p>
      </div>

      <details className="mt-3 text-sm">
        <summary className="min-h-9 cursor-pointer font-semibold">
          How to add it in Claude
        </summary>
        <ol className="ml-4 mt-1 list-decimal space-y-1 text-muted-foreground">
          <li>Settings → Connectors → Add custom connector.</li>
          <li>Paste the endpoint above.</li>
          <li>Sign in to Servwell and approve the read-only tools.</li>
          <li>Ask it one of the questions below.</li>
        </ol>
      </details>
      <details className="text-sm">
        <summary className="min-h-9 cursor-pointer font-semibold">
          How to add it in ChatGPT
        </summary>
        <ol className="ml-4 mt-1 list-decimal space-y-1 text-muted-foreground">
          <li>Turn on custom connectors in settings.</li>
          <li>Add a connector with the endpoint above and sign in.</li>
          <li>Read-only connectors work on Plus and Pro.</li>
        </ol>
      </details>

      <div className="mt-3">
        <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          Things worth asking
        </p>
        <ul className="space-y-1 text-sm">
          {TRY_IT.map((p) => (
            <li
              key={p}
              className="rounded-[var(--radius-sm)] border border-border bg-surface-2 px-2.5 py-1.5"
            >
              {p}
            </li>
          ))}
        </ul>
      </div>

      {isOwner ? (
        <div className="mt-3 border-t border-border pt-3">
          <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            What any AI may read
          </p>
          {/* Shop-wide, and settable BEFORE anything is connected: deciding "no AI ever
              reads our money" should not require first connecting an AI to say it to.
              An off switch here beats every per-connection toggle. */}
          <p className="mb-2 text-xs text-muted-foreground">
            Switch something off here and it's off for every connection, now and later —
            including ones somebody else adds.
          </p>
          <div className="flex flex-wrap gap-1.5">
            {MCP_TOOL_NAMES.map((tool) => {
              const off = orgDisabled.includes(tool);
              return (
                <button
                  key={tool}
                  type="button"
                  disabled={toggleOrgTool.isPending}
                  onClick={() => toggleOrgTool.mutate({ tool, enabled: off })}
                  className={`rounded-full border px-2.5 py-0.5 text-xs font-medium disabled:opacity-40 ${
                    off
                      ? 'border-border bg-surface-2 text-muted-foreground line-through'
                      : 'border-brand bg-brand/10 text-text'
                  }`}
                  title={
                    off
                      ? 'Switched off for the whole shop'
                      : 'Switch this off for everyone'
                  }
                >
                  {tool}
                </button>
              );
            })}
          </div>
        </div>
      ) : null}

      <div className="mt-3 border-t border-border pt-3">
        <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          What's connected
        </p>
        {connections.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            Nothing yet. Add the endpoint above and it shows up here the moment it
            connects — with everything it has asked for.
          </p>
        ) : (
          <ul className="space-y-2">
            {connections.map((c) => (
              <li
                key={`${c.clientId}:${c.connectedByUserId}`}
                className="space-y-2 rounded-[var(--radius)] border border-border p-2.5"
              >
                <div className="flex flex-wrap items-start justify-between gap-2">
                  <div className="min-w-0">
                    <p className="font-semibold">{c.clientName ?? c.clientId}</p>
                    <p className="text-xs text-muted-foreground">
                      Connected by {c.connectedByName ?? 'a member'} · {c.memberRole} ·{' '}
                      {c.callCount30d} question{c.callCount30d === 1 ? '' : 's'} in 30
                      days ·{' '}
                      {c.lastCallAt
                        ? `last ${new Date(c.lastCallAt).toLocaleString()}`
                        : 'never used'}
                    </p>
                  </div>
                  {isOwner ? (
                    <Button
                      variant="ghost"
                      disabled={disconnect.isPending}
                      onClick={() =>
                        disconnect.mutate({
                          clientId: c.clientId,
                          connectedByUserId: c.connectedByUserId,
                        })
                      }
                    >
                      Cut it off
                    </Button>
                  ) : null}
                </div>
                {isOwner ? (
                  <div className="flex flex-wrap gap-1.5">
                    {c.tools.map((tool) => (
                      <button
                        key={tool.name}
                        type="button"
                        disabled={tool.disabled_org_wide || toggleTool.isPending}
                        onClick={() =>
                          toggleTool.mutate({
                            clientId: c.clientId,
                            connectedByUserId: c.connectedByUserId,
                            tool: tool.name,
                            enabled: !tool.enabled,
                          })
                        }
                        className={`rounded-full border px-2.5 py-0.5 text-xs font-medium disabled:opacity-40 ${
                          tool.enabled
                            ? 'border-brand bg-brand/10 text-text'
                            : 'border-border bg-surface-2 text-muted-foreground line-through'
                        }`}
                        title={
                          tool.disabled_org_wide
                            ? 'Switched off for the whole shop'
                            : 'Switch this off for this connection'
                        }
                      >
                        {tool.name}
                      </button>
                    ))}
                  </div>
                ) : null}
              </li>
            ))}
          </ul>
        )}
        {!isOwner && connections.length > 0 ? (
          <p className="mt-1 text-xs text-muted-foreground">
            Only the owner can cut a connection off or change what it can see.
          </p>
        ) : null}
      </div>
    </SectionCard>
  );
}

/** The org's own details. Timezone is load-bearing — it decides what "today" means. */
export function ShopDetailsCard({ id }: { id: string }) {
  const trpc = useTRPC();
  const qc = useQueryClient();
  const { role } = useSession();
  const q = useQuery(trpc.org.info.queryOptions());
  const [tz, setTz] = useState<string | null>(null);
  const [addr, setAddr] = useState<string | null>(null);
  const [taxPct, setTaxPct] = useState<string | null>(null);
  const [laborRate, setLaborRate] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const save = useMutation(
    trpc.org.updateProfile.mutationOptions({
      onSuccess: () => {
        setError(null);
        setTz(null);
        setAddr(null);
        setTaxPct(null);
        setLaborRate(null);
        void qc.invalidateQueries();
      },
      onError: (e) => setError(e.message),
    }),
  );
  // The labor switches save at once, on their own: a flip never clears a rate, an address or a
  // tax being typed beside it.
  const saveSwitch = useMutation(
    trpc.org.updateProfile.mutationOptions({
      onSuccess: () => {
        setError(null);
        void qc.invalidateQueries();
      },
      onError: (e) => setError(e.message),
    }),
  );

  /**
   * The shell stays even with nothing to put in it: `#your-shop` lives on it, and the router
   * looks for the element exactly once. Replacing the whole card on an error deletes the
   * anchor along with the content — 011 fixed that and deliberately left WHAT it says to
   * this ticket.
   *
   * What 011's version could not tell apart: `if (!org)` answered "the server had a bad day",
   * "you may not see this" and "you have no connection" with the same sentence and the same
   * Try-again button — and for a 403 that button is the one action guaranteed to fail. The
   * contract asks `isForbidden` once, in one place, for all fifteen cards.
   */
  const state = shownState(q, q.data);
  if (!q.data) {
    return (
      <SectionCard id={id} title="Your shop">
        <UnresolvedState
          state={unresolved(state)}
          what="your shop's details"
          icon={IconSettings}
          error={q.error}
          onRetry={() => void q.refetch()}
          skeleton="h-16"
        />
      </SectionCard>
    );
  }
  const org = q.data;

  const browserTz = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const current = tz ?? org.timezone;
  const currentAddr = addr ?? org.postalAddress ?? '';
  // '' is the honest rendering of "not set" — NOT '0', which would claim the shop had
  // answered the question. The two are different answers and stay different.
  const savedTax =
    org.defaultTaxRateBps === null || org.defaultTaxRateBps === undefined
      ? ''
      : (org.defaultTaxRateBps / 100).toString();
  const currentTax = taxPct ?? savedTax;
  // The shop's hourly labor rate (Inventory v2 ticket 13), in dollars; '' for none.
  const savedRate =
    org.laborRateCents === null || org.laborRateCents === undefined
      ? ''
      : (org.laborRateCents / 100).toFixed(2);
  const currentRate = laborRate ?? savedRate;
  const rateCents =
    currentRate.trim() === '' ? null : Math.round(Number(currentRate) * 100);
  const rateOk =
    currentRate.trim() === '' ||
    (Number.isFinite(Number(currentRate)) &&
      rateCents !== null &&
      rateCents >= 1 &&
      rateCents <= 100_000_000 &&
      Math.abs(Number(currentRate) * 100 - rateCents) < 1e-6);
  const mismatch = browserTz && browserTz !== org.timezone && tz === null;

  return (
    <SectionCard id={id} title="Your shop">
      <StaleNotice show={refreshFailed(q, q.data)} onRetry={() => void q.refetch()} />
      {error ? <Notice tone="danger">{error}</Notice> : null}
      <dl className="space-y-2 text-sm">
        <div className="flex flex-wrap items-baseline justify-between gap-2">
          <dt className="text-muted-foreground">Name</dt>
          <dd className="font-medium">{org.name}</dd>
        </div>
        <div className="flex flex-wrap items-baseline justify-between gap-2">
          <dt className="text-muted-foreground">Plan</dt>
          <dd>
            <StatusBadge
              tone={org.subscriptionStatus === 'canceled' ? 'danger' : 'success'}
            >
              {org.subscriptionStatus}
            </StatusBadge>
          </dd>
        </div>
      </dl>

      <div className="mt-3 border-t border-border pt-3">
        <label className="flex flex-col gap-1 text-sm">
          <span className="font-medium">Your timezone</span>
          <span className="text-xs text-muted-foreground">
            This decides what "today" means on the schedule and when the overnight billing
            run happens. Get it wrong and a job shows on the wrong day.
          </span>
          <div className="mt-1 flex flex-wrap gap-2">
            <select
              className="h-10 min-w-0 flex-1 rounded-[var(--radius)] border border-border bg-surface px-2 text-sm"
              aria-label="Timezone"
              value={current}
              disabled={role !== 'owner' || save.isPending}
              onChange={(e) => setTz(e.target.value)}
            >
              {[...new Set([org.timezone, browserTz, ...US_ZONES])]
                .filter(Boolean)
                .map((z) => (
                  <option key={z} value={z}>
                    {z}
                  </option>
                ))}
            </select>
            {tz && tz !== org.timezone ? (
              <Button
                disabled={save.isPending}
                onClick={() => save.mutate({ timezone: tz })}
              >
                {save.isPending ? 'Saving…' : 'Save'}
              </Button>
            ) : null}
          </div>
        </label>
        {/* Said out loud rather than silently applied — a timezone that changes itself is
            how a shop's whole schedule moves without anyone deciding to move it. */}
        {mismatch ? (
          <div className="mt-2">
            <Notice tone="warn">
              This computer is set to {browserTz}, but the shop is set to {org.timezone}.
              If the shop moved, change it here — it won't change on its own.
            </Notice>
          </div>
        ) : null}
        {role !== 'owner' ? (
          <p className="mt-1 text-xs text-muted-foreground">
            Only the owner can change this.
          </p>
        ) : null}
      </div>

      <div className="mt-3 border-t border-border pt-3">
        <label className="flex flex-col gap-1 text-sm">
          <span className="font-medium">Sales tax rate</span>
          <span className="text-xs text-muted-foreground">
            Fills in on every quote and invoice, so nobody has to remember it. You can
            still change it on any one document — for a resale, or a job over a state
            line.
          </span>
          <div className="mt-1 flex flex-wrap items-center gap-2">
            <input
              className="h-10 w-28 rounded-[var(--radius)] border border-border bg-surface px-2 text-sm"
              aria-label="Sales tax rate percent"
              value={currentTax}
              inputMode="decimal"
              placeholder="8.25"
              disabled={role !== 'owner' || save.isPending}
              onChange={(e) => setTaxPct(e.target.value)}
            />
            <span className="text-sm text-muted-foreground">%</span>
            {taxPct !== null && taxPct !== savedTax ? (
              <Button
                disabled={save.isPending}
                onClick={() =>
                  save.mutate({
                    // Clearing the box puts the shop back to "not set", which the money
                    // screens read differently from a deliberate 0%.
                    defaultTaxRateBps:
                      taxPct.trim() === '' ? null : Math.round(Number(taxPct) * 100),
                  })
                }
              >
                {save.isPending ? 'Saving…' : 'Save'}
              </Button>
            ) : null}
          </div>
        </label>
        {/* The whole reason this setting exists: an unanswered question was quietly being
            answered as 0% on every invoice. */}
        {savedTax === '' ? (
          <div className="mt-2">
            <Notice tone="warn">
              No rate set, so quotes and invoices bill at 0%. If your shop charges sales
              tax, set it here — it can't be added to an invoice a customer already has.
            </Notice>
          </div>
        ) : null}
      </div>

      {/* Inventory v2 ticket 13: what a T&M job's hours start at, whether labor is taxed, and
          whether customers see hours. Each service type may carry its own rate; this is the
          shop's, for a type with none. */}
      <div className="mt-3 border-t border-border pt-3">
        <p className="text-sm font-medium">Labor on T&amp;M jobs</p>
        <label className="mt-2 flex flex-col gap-1 text-sm">
          <span className="text-xs text-muted-foreground">
            The hourly rate a T&amp;M job's hours start at when its service type has no
            rate of its own (set rates per type under Service types). Blank: the office
            types a rate when billing.
          </span>
          <div className="mt-1 flex flex-wrap items-center gap-2">
            <span className="text-sm text-muted-foreground" aria-hidden>
              $
            </span>
            <input
              className="h-10 w-28 rounded-[var(--radius)] border border-border bg-surface px-2 text-sm"
              aria-label="Shop labor rate per hour"
              aria-invalid={rateOk ? undefined : true}
              aria-describedby={rateOk ? undefined : 'shop-labor-rate-error'}
              value={currentRate}
              inputMode="decimal"
              placeholder="125.00"
              disabled={role !== 'owner' || save.isPending}
              onChange={(e) => setLaborRate(e.target.value)}
            />
            <span className="text-sm text-muted-foreground">/h</span>
            {laborRate !== null && laborRate !== savedRate ? (
              <Button
                disabled={save.isPending || !rateOk}
                onClick={() => save.mutate({ laborRateCents: rateCents })}
              >
                {save.isPending ? 'Saving…' : 'Save'}
              </Button>
            ) : null}
          </div>
          {rateOk ? null : (
            <span
              id="shop-labor-rate-error"
              className="text-xs text-[var(--sw-danger-text)]"
            >
              Type an hourly rate in dollars, like 125 or 125.50.
            </span>
          )}
        </label>
        <div className="mt-2 flex flex-col gap-1">
          <Toggle
            label="Charge sales tax on labor"
            on={org.laborTaxable}
            disabled={role !== 'owner' || saveSwitch.isPending}
            focusableWhenDisabled
            onChange={(laborTaxable) => saveSwitch.mutate({ laborTaxable })}
          />
          <span className="text-xs text-muted-foreground">
            Off, labor lines aren't taxed. Some states tax repair labor: check yours.
          </span>
          <Toggle
            label="Show hours on invoices"
            on={org.showHoursOnInvoices}
            disabled={role !== 'owner' || saveSwitch.isPending}
            focusableWhenDisabled
            onChange={(showHoursOnInvoices) => saveSwitch.mutate({ showHoursOnInvoices })}
          />
          <span className="text-xs text-muted-foreground">
            Off, the customer sees one Labor line with its amount, not hours or a rate.
            Turn hours on from an invoice's draft when a job is billed by the hour.
          </span>
        </div>
      </div>

      <div className="mt-3 border-t border-border pt-3">
        <label className="flex flex-col gap-1 text-sm">
          <span className="font-medium">Your mailing address</span>
          {/* Not cosmetic: a promotional email may not go without it (CAN-SPAM), so the
              reason is stated here rather than discovered as an email that did not go. */}
          <span className="text-xs text-muted-foreground">
            A real, physical address — the law requires one in any marketing email, so the
            automatic review requests and service reminders won't go without it. It
            appears at the bottom of those emails and on your invoice and quote pages, as
            you write it, so it can't carry a phone number or a web or email address, or
            pose as a brand.
          </span>
          <textarea
            className="mt-1 min-h-20 rounded-[var(--radius)] border border-border bg-surface p-2.5 text-sm"
            aria-label="Mailing address"
            value={currentAddr}
            disabled={role !== 'owner' || save.isPending}
            placeholder={'12 Depot St\nMarion, OH 43302'}
            onChange={(e) => setAddr(e.target.value)}
          />
        </label>
        {addr !== null && addr !== (org.postalAddress ?? '') ? (
          <Button
            className="mt-2"
            disabled={save.isPending}
            onClick={() => save.mutate({ postalAddress: addr })}
          >
            {save.isPending ? 'Saving…' : 'Save address'}
          </Button>
        ) : null}
      </div>
    </SectionCard>
  );
}

/** The zones a US home-service shop actually operates in. */
const US_ZONES = [
  'America/New_York',
  'America/Chicago',
  'America/Denver',
  'America/Phoenix',
  'America/Los_Angeles',
  'America/Anchorage',
  'Pacific/Honolulu',
];
